// Maps Salesforce Opportunity records into Bullogic Pipeline prospect format.
// Uses naming conventions to infer division/serviceTier from SF stage and source.

// Map SF stage names to our pipeline stage IDs
// Customize these to match your exact SF stage names
const STAGE_MAP = {
  // Common SF defaults
  'Prospecting': 'discovery-scheduled',
  'Qualification': 'discovery-complete',
  'Needs Analysis': 'discovery-complete',
  'Value Proposition': 'strategy-scheduled',
  'Id. Decision Makers': 'strategy-scheduled',
  'Perception Analysis': 'strategy-complete',
  'Proposal/Price Quote': 'proposal',
  'Negotiation/Review': 'proposal',
  'Closed Won': 'closed-won',
  'Closed Lost': 'closed-lost',

  // Bullogic-specific stages (matching our pipeline naming)
  'Discovery Scheduled': 'discovery-scheduled',
  'Discovery Complete': 'discovery-complete',
  'Strategy Scheduled': 'strategy-scheduled',
  'Strategy Complete': 'strategy-complete',
  'Proposal': 'proposal',
};

// Map SF Lead Source to our source values
const SOURCE_MAP = {
  'Web': 'website',
  'Website': 'website',
  'Phone Inquiry': 'website',
  'Partner Referral': 'referral',
  'Customer Referral': 'referral',
  'Referral': 'referral',
  'Zoe Financial': 'solicitor',
  'Solicitor': 'solicitor',
  'Podcast': 'podcast',
  'Cross-Sell': 'cross-sell',
  'Cross Sell': 'cross-sell',
  'Existing Client': 'cross-sell',
  'Existing Customer': 'cross-sell',
  'Other': 'other',
};

// Detect Tax Plan specifically (a BWM service, not BTS).
// Order matters: check Tax Plan BEFORE the generic 'tax' check so it routes correctly.
function isTaxPlan(opp) {
  const rtName = (opp.RecordType?.Name || opp.RecordType?.DeveloperName || '').toLowerCase();
  const oppType = (opp.Type || '').toLowerCase();
  const name = (opp.Name || '').toLowerCase();
  if (rtName.includes('tax plan') || rtName.includes('taxplan')) return true;
  if (oppType.includes('tax plan') || oppType.includes('taxplan')) return true;
  if (name.includes('tax plan') || name.includes('taxplan')) return true;
  return false;
}

// Detect Investment Management (BWM > Investment Only tier with AUM fee)
function isInvestmentManagement(opp) {
  const rtName = (opp.RecordType?.Name || opp.RecordType?.DeveloperName || '').toLowerCase();
  const oppType = (opp.Type || '').toLowerCase();
  if (rtName.includes('investment management') || rtName.includes('investmentmanagement')) return true;
  if (rtName.includes('investment only') || rtName.includes('investmentonly')) return true;
  if (oppType.includes('investment management') || oppType.includes('investmentmanagement')) return true;
  return false;
}

// Default AUM fee rate (0.5%)
const DEFAULT_AUM_FEE_RATE = 0.005;

// Infer division from multiple signals: RecordType, Type, name keywords, account type
function inferDivision(opp) {
  // 0. Tax Plan and Investment Management are BWM - check FIRST before generic matching
  if (isTaxPlan(opp)) return 'bwm';
  if (isInvestmentManagement(opp)) return 'bwm';

  // 1. Strongest signal: RecordType (common SF practice)
  const rtName = (opp.RecordType?.Name || opp.RecordType?.DeveloperName || '').toLowerCase();
  if (rtName) {
    if (rtName.includes('tax') || rtName.includes('bts') || rtName.includes('bookkeeping')) return 'bts';
    if (rtName.includes('insurance') || rtName.includes('bia')) return 'bia';
    if (rtName.includes('wealth') || rtName.includes('bwm') || rtName.includes('financial')) return 'bwm';
  }

  // 2. Type field (sometimes used to categorize)
  const oppType = (opp.Type || '').toLowerCase();
  if (oppType) {
    if (oppType.includes('tax') || oppType.includes('bookkeeping')) return 'bts';
    if (oppType.includes('insurance')) return 'bia';
    if (oppType.includes('wealth') || oppType.includes('financial planning')) return 'bwm';
  }

  // 3. Name keywords (broader pattern matching)
  const name = (opp.Name || '').toLowerCase();
  if (name.match(/\b(tax|bookkeeping|return|1040|1120|1065|s-corp|tax prep)\b/)) return 'bts';
  if (name.match(/\b(insurance|policy|life|disability|long.term care|annuity)\b/)) return 'bia';
  if (name.match(/\b(wealth|financial planning|guided wealth|total wealth|aum|investment)\b/)) return 'bwm';

  // 4. Account Type (if present in SF Account record)
  const acctType = (opp.Account?.Type || '').toLowerCase();
  if (acctType.includes('tax')) return 'bts';
  if (acctType.includes('insurance')) return 'bia';

  return 'bwm'; // default fallback
}

// Infer service tier from amount + division
function inferServiceTier(opp, division) {
  const amount = opp.Amount || 0;
  if (division === 'bwm') {
    // Tax Plan opportunities map to the tax-plan tier
    if (isTaxPlan(opp)) return 'tax-plan';
    // Investment Management opportunities map to investment-only tier
    if (isInvestmentManagement(opp)) return 'investment-only';
    // BWM monthly fees: <$1,200/mo annually = guided wealth ($650 * 12 = $7,800)
    // > $1,200/mo annually = total wealth
    if (amount === 0) return 'investment-only';
    if (amount > 14400) return 'total-wealth'; // >$1,200/mo
    return 'guided-wealth';
  }
  if (division === 'bts') {
    if (amount >= 2000) return 'tax-business';
    return 'tax-individual';
  }
  return 'insurance';
}

// Estimate monthly recurring + one-time + AUM from SF Amount and custom fields.
// SF typically stores annual amount or one-time deal value.
function parseEstimatedValue(opp, division, serviceTier) {
  const amount = opp.Amount || 0;

  if (division === 'bwm' && (serviceTier === 'guided-wealth' || serviceTier === 'total-wealth')) {
    // Treat as monthly recurring (SF amount is annual)
    return {
      monthlyRecurring: Math.round(amount / 12),
      oneTime: 0,
      aum: 0,
      aumFeeRate: 0,
    };
  }

  // Investment Management: AUM = Rollover Amount, fee = AUM * 0.5% (default rate)
  if (serviceTier === 'investment-only') {
    const rolloverAmount = opp.RolloverAmount || amount || 0;
    return {
      monthlyRecurring: 0,
      oneTime: 0,
      aum: rolloverAmount,
      aumFeeRate: DEFAULT_AUM_FEE_RATE,
    };
  }

  // Tax plan, tax services, insurance: treat as one-time amount
  return {
    monthlyRecurring: 0,
    oneTime: amount,
    aum: 0,
    aumFeeRate: 0,
  };
}

/**
 * Transform a Salesforce Opportunity record into our prospect format.
 * @param {Object} opp - SF Opportunity from query
 * @returns {Object} Bullogic prospect
 */
export function mapSFOpportunity(opp) {
  const stage = STAGE_MAP[opp.StageName] || 'discovery-scheduled';
  const source = SOURCE_MAP[opp.LeadSource] || 'other';
  const division = inferDivision(opp);
  const serviceTier = inferServiceTier(opp, division);
  const estimatedValue = parseEstimatedValue(opp, division, serviceTier);

  let outcome = null;
  let lossReason = null;
  if (opp.IsClosed) {
    outcome = opp.IsWon ? 'won' : 'lost';
  }

  // Extract primary contact info from OpportunityContactRoles subquery.
  // SF returns these as opp.OpportunityContactRoles.records (array). The
  // SOQL ordered by IsPrimary DESC, so records[0] is the primary contact
  // (or the first one available if none is explicitly primary).
  const contactRoles = opp.OpportunityContactRoles?.records || [];
  const primaryRole = contactRoles.find(r => r.IsPrimary) || contactRoles[0] || null;
  const primaryEmail = primaryRole?.Contact?.Email || '';
  const primaryPhone = primaryRole?.Contact?.Phone || opp.Account?.Phone || '';
  const primaryContactName = primaryRole?.Contact?.Name || '';

  return {
    // Use SF Id prefixed so we can identify SF-synced prospects
    id: `sf-${opp.Id}`,
    sfId: opp.Id,
    name: opp.Account?.Name || opp.Name,
    company: opp.Account?.Name && opp.Account.Name !== opp.Name ? opp.Account.Name : '',
    email: primaryEmail,
    phone: primaryPhone,
    primaryContactName,
    division,
    serviceTier,
    stage,
    source,
    sourceDetail: opp.LeadSource || '',
    estimatedValue,
    // SF's stored Probability (0-100 integer). Used by SFSyncButton to skip
    // the probability push-back when our effective probability already
    // matches what SF has — without this, every sync PATCHed every
    // opportunity (hundreds of calls per 5-min auto-sync).
    sfProbability: opp.Probability != null ? Math.round(Number(opp.Probability)) : null,
    expectedCloseDate: opp.CloseDate,
    createdDate: opp.CreatedDate?.split('T')[0] || new Date().toISOString().split('T')[0],
    closedDate: opp.IsClosed ? opp.CloseDate : null,
    outcome,
    lossReason,
    notes: opp.Description || '',
    owner: opp.Owner?.Name || '',
    // Empty stage history (we don't have it from SF unless we query history table)
    stageHistory: [],
  };
}

/**
 * Map array of SF opportunities to prospects, filter out invalid ones.
 */
export function mapSFOpportunities(records) {
  if (!Array.isArray(records)) return [];
  return records
    .filter(opp => opp.Id && opp.Name)
    .map(mapSFOpportunity);
}
